import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { UtilitiesService } from '@services/utilities.service';
import { BaseDatasource } from '@shared/base/base.datasource';
import { BaseResponse } from '@shared/base/base-response';
import { TableColumn } from '@shared/interface/table.interface';
import { BottomTableComponent } from '@shared/components/bottom-table/bottom-table.component';
import { CardEmptyComponent } from '@shared/components/card';
import { SectionLoaderComponent } from '@shared/components/loader';
import { ModalFormComponent } from '@shared/components/modal/modal-form/modal-form.component';
import { ModalConfig } from '@shared/components/modal/modal.interface';
import { TableContentComponent } from '@shared/components/table-content/table-content.component';
import { TargetDistributorService } from '../../../target-distributor.service';
import { ITargetDistributorMonitoring__PotentialPo } from '../../../target-distributor-monitoring.interface';
import { createMonitoringPaginatorIntl } from '../../monitoring-paginator-intl';
import { MONITORING_PAGINATOR_ROUTE_PROVIDERS, MonitoringPaginatorRoute } from '../../monitoring-paginator-route';

/** Payload berbentuk objek, mengikuti `IModalDetailDistributorPayload`. */
export interface IModalPotentialPoPayload {
  targetDistributorId: string;
  distributorId: string;
  distributorName: string;
}

const DEFAULT_PAGE_SIZE = 10;

/**
 * [AC-02] Urutan tabel: nilai PO tertinggi di atas, ditentukan backend.
 *
 * Header tidak sortable — QA (MAI-2608) meminta ikon asc/desc pada kolom Tanggal Expired
 * dihapus, jadi urutan tabel tetap memakai nilai default ini.
 */
const DEFAULT_SORT = 'po_value,desc';

/**
 * Sub-menu PO yang dipakai saat membuka halaman Detail PO dari kolom Action.
 *
 * Route detail berbentuk `/purchase-order/{subMenu}/{statusPurchaseOrder}/{id}`. Sama seperti
 * `purchase-order-list`, entry point dari luar modul PO memakai sub-menu `list` (Semua PO)
 * karena baris di tabel ini bisa berstatus Siap Diproses maupun Diproses Sebagian.
 */
const PO_DETAIL_SUBMENU = 'list';

/**
 * [MAI-2608] Sub-modal "PO [Nama Distributor]".
 *
 * Dibuka dari tombol "Lihat PO" pada banner potensi PO di modal Detail per Distributor.
 * Modal induk dibiarkan terbuka di belakang, sehingga menutup modal ini (X) langsung
 * mengembalikan pengguna ke modal Detail per Distributor (AC-04).
 *
 * Nilai PO, QTY, dan berat datang sudah terformat dari backend — tidak ada formatting di
 * frontend. Satu-satunya nilai mentah adalah `expired_date` (ISO 8601), yang diformat
 * memakai `UtilitiesService` seperti kolom Tanggal Expired di halaman daftar PO.
 */
@Component({
  selector: 'app-modal-potential-po',
  standalone: true,
  imports: [CommonModule, MatTableModule, InlineSVGModule, ModalFormComponent, SectionLoaderComponent, CardEmptyComponent, TableContentComponent, BottomTableComponent],
  providers: [
    // Paginator memakai `ActivatedRoute` lokal supaya indeks halamannya tidak ikut
    // query param `page` milik tabel distributor di halaman induk.
    ...MONITORING_PAGINATOR_ROUTE_PROVIDERS,
    // Instance sendiri supaya label tidak bergantung pada `MatPaginatorIntl` global.
    { provide: MatPaginatorIntl, useFactory: createMonitoringPaginatorIntl },
  ],
  templateUrl: './modal-potential-po.component.html',
  styleUrls: ['./modal-potential-po.component.scss'],
})
export class ModalPotentialPoComponent {
  @ViewChild('modal') private modal!: ModalFormComponent;

  /**
   * Modal ditutup (X atau backdrop) — pemanggil memakai ini untuk menampilkan kembali
   * modal induk yang disembunyikan saat modal ini dibuka.
   */
  @Output() closed = new EventEmitter<void>();

  readonly STRING_CONSTANTS = STRING_CONSTANTS;
  readonly dataSource = new BaseDatasource<ITargetDistributorMonitoring__PotentialPo>();
  readonly tableColumns: TableColumn[] = [
    { key: 'po_code', title: 'PO ID', isSortable: false },
    { key: 'po_value', title: 'Nilai PO', isSortable: false },
    { key: 'total_qty', title: 'Total QTY', isSortable: false },
    { key: 'status_string', title: 'Status', isSortable: false },
    { key: 'expired_date', title: 'Tanggal Expired', isSortable: false },
    { key: 'actions', title: 'Action', isSortable: false },
  ];
  readonly displayedColumns = this.tableColumns.map((column) => column.key);

  payload: IModalPotentialPoPayload | null = null;
  /** Loader penuh hanya untuk muat pertama; perpindahan halaman memakai skeleton per sel. */
  isFirstLoad = true;
  hasError = false;

  /** `showFooter: false` — design tidak punya tombol aksi di bawah modal. */
  modalConfig: ModalConfig = {
    modalTitle: '',
    showHeader: true,
    showFooter: false,
  };

  /** Lebar 1000px, kerangka sama dengan modal induk — diatur lewat `windowClass` di file scss. */
  readonly modalOptions: NgbModalOptions = {
    windowClass: 'modal-potential-po',
    scrollable: true,
  };

  constructor(
    private readonly targetDistributorService: TargetDistributorService,
    private readonly paginatorRoute: MonitoringPaginatorRoute,
    private readonly router: Router,
    public readonly utilities: UtilitiesService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {
    this.dataSource.pageSizeSelect = [10, 25, 50];
    this.dataSource.pageSize = DEFAULT_PAGE_SIZE;
  }

  /**
   * `detectChanges()` sebelum `open()` wajib: NgbModal baru meng-instantiate `<ng-template>`
   * saat `open()` dipanggil, sehingga judul harus sudah ter-render lebih dulu.
   */
  open(payload: IModalPotentialPoPayload): void {
    this.payload = payload;
    this.hasError = false;
    this.isFirstLoad = true;
    this.dataSource.current_page = 0;
    this.dataSource.pageSize = DEFAULT_PAGE_SIZE;
    // Paginator ikut dikembalikan ke halaman pertama; kalau tidak, membuka modal untuk
    // distributor lain akan menyorot halaman terakhir yang dipakai sebelumnya.
    this.paginatorRoute.setPage(0);
    this.modalConfig = { ...this.modalConfig, modalTitle: `PO ${payload.distributorName}` };
    // NgbModal menandai modal induk `aria-hidden` begitu sub-modal terbuka. Kalau tombol
    // pemicu ("Lihat PO") masih ter-fokus, browser menolak aria-hidden itu dan mencatat
    // warning a11y — jadi fokusnya dilepas dulu.
    (document.activeElement as HTMLElement | null)?.blur();
    this.cdr.detectChanges();
    // `open()` resolve saat modal ditutup/di-dismiss — dipakai untuk memunculkan kembali
    // modal induk, termasuk saat modal ditutup bukan lewat method `close()` di bawah.
    this.modal.open().then(() => this.closed.emit());
    this.fetchPotentialPo();
  }

  close(): void {
    this.modal.close();
  }

  get isEmpty(): boolean {
    return this.dataSource.isTableLoaded && !this.dataSource.hasItems;
  }

  onPageChange(source: BaseDatasource<ITargetDistributorMonitoring__PotentialPo>): void {
    this.dataSource.pageSize = source.pageSize;
    this.goToPage(source.current_page);
  }

  /**
   * [AC-03] Ikon Action membuka halaman Detail PO. Dibuka di tab baru supaya dua modal yang
   * sedang terbuka (Detail per Distributor + PO) tidak hilang — pola yang sama dipakai
   * sub-modal Nilai Invoice (MAI-2604) saat membuka Detail SO.
   */
  onDetailPo(row: ITargetDistributorMonitoring__PotentialPo): void {
    if (!row.po_id) return;

    const url = this.router.serializeUrl(this.router.createUrlTree([`/purchase-order/${PO_DETAIL_SUBMENU}/${row.status_enum}/${row.po_id}`]));
    window.open(url, '_blank');
  }

  private goToPage(page: number): void {
    this.dataSource.current_page = page;
    // Paginator membaca halaman dari route lokal ini, bukan dari URL halaman induk.
    this.paginatorRoute.setPage(page);
    this.fetchPotentialPo();
  }

  private fetchPotentialPo(): void {
    if (!this.payload) return;

    this.dataSource.isFinishLoadingSubject.next(false);

    this.targetDistributorService
      .getMonitoringPotentialPoList(this.payload.targetDistributorId, this.payload.distributorId, {
        page: this.dataSource.current_page,
        size: this.dataSource.pageSize,
        sort: DEFAULT_SORT,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.applyPotentialPo(res),
        error: () => this.applyPotentialPo(null),
      });
  }

  private applyPotentialPo(res: BaseResponse<ITargetDistributorMonitoring__PotentialPo[]> | null): void {
    const rows = res?.data ?? [];

    // Gagal saat muat pertama = tidak ada apa pun untuk ditampilkan; kegagalan saat pindah
    // halaman cukup jatuh ke empty state supaya tabel tidak ikut hilang.
    this.hasError = !res && this.isFirstLoad;

    this.dataSource.tableSubject.next(rows);
    this.dataSource.totalItem = res?.total_data ?? 0;
    this.dataSource.totalPage = res?.total_page ?? 0;
    this.dataSource.current_page = res?.current_page ?? 0;
    this.dataSource.hasItems = rows.length > 0;
    this.dataSource.isFinishLoadingSubject.next(true);
    this.isFirstLoad = false;
    this.cdr.detectChanges();
  }
}
